import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BookingSuccess = ({ bookingDetails }) => {
  const navigate = useNavigate();

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const handleWhatsAppShare = () => {
    const message = `Booking Confirmed!\nReference: ${bookingDetails?.referenceNumber}\nRoute: ${bookingDetails?.route}\nDate: ${formatDate(bookingDetails?.pickupDate)}\nTime: ${bookingDetails?.pickupTime}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleDownloadReceipt = () => {
    alert('Receipt download functionality will be implemented with backend integration');
  };

  return (
    <div className="bg-card rounded-lg p-4 md:p-6 lg:p-8 shadow-sm space-y-6 md:space-y-8">
      <div className="text-center space-y-4">
        <div className="w-16 h-16 md:w-20 md:h-20 mx-auto bg-success/10 rounded-full flex items-center justify-center animate-bounce">
          <Icon name="CheckCircle" size={40} className="text-success" strokeWidth={2.5} />
        </div>
        
        <div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
            Booking Confirmed!
          </h2>
          <p className="text-sm md:text-base text-muted-foreground">
            Your journey is all set. We've sent confirmation details to your email and mobile. For any queries, contact us at advaittourtravel666@gmail.com or call +91-79986-66660.
          </p>
        </div>

        <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-lg">
          <Icon name="Hash" size={18} className="text-primary" />
          <span className="text-sm font-mono font-semibold text-primary">
            {bookingDetails?.referenceNumber}
          </span>
        </div>
      </div>
      <div className="space-y-4">
        <div className="bg-gradient-journey rounded-lg p-4 md:p-6 text-white space-y-4">
          <h3 className="text-lg md:text-xl font-semibold">Trip Summary</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-xs text-white/70">Service Type</p>
              <p className="text-sm font-semibold">{bookingDetails?.serviceType}</p>
            </div>
            
            <div className="space-y-1">
              <p className="text-xs text-white/70">Vehicle</p>
              <p className="text-sm font-semibold">{bookingDetails?.vehicleType}</p>
            </div>
            
            <div className="space-y-1">
              <p className="text-xs text-white/70">Route</p>
              <p className="text-sm font-semibold">{bookingDetails?.route}</p>
            </div>
            
            <div className="space-y-1">
              <p className="text-xs text-white/70">Passengers</p>
              <p className="text-sm font-semibold">{bookingDetails?.passengers} persons</p>
            </div>
          </div>

          <div className="pt-4 border-t border-white/20 space-y-3">
            <div className="flex items-center gap-3">
              <Icon name="Calendar" size={18} />
              <div>
                <p className="text-xs text-white/70">Pickup Date & Time</p>
                <p className="text-sm font-semibold">
                  {formatDate(bookingDetails?.pickupDate)} at {bookingDetails?.pickupTime}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Icon name="MapPin" size={18} />
              <div>
                <p className="text-xs text-white/70">Pickup Location</p>
                <p className="text-sm font-semibold line-clamp-2">{bookingDetails?.pickupAddress}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-muted/50 rounded-lg p-4 space-y-3">
          <h4 className="text-sm font-semibold text-foreground">Contact Information</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <Icon name="User" size={16} className="text-primary" />
              <span className="text-muted-foreground">{bookingDetails?.fullName}</span>
            </div>
            
            <div className="flex items-center gap-2">
              <Icon name="Phone" size={16} className="text-primary" />
              <span className="text-muted-foreground">{bookingDetails?.mobile}</span>
            </div>
            
            <div className="flex items-center gap-2 md:col-span-2">
              <Icon name="Mail" size={16} className="text-primary" />
              <span className="text-muted-foreground">{bookingDetails?.email}</span>
            </div>
          </div>
        </div>

        <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">Total Amount Paid</span>
            <span className="text-xl md:text-2xl font-bold text-primary">
              {formatCurrency(bookingDetails?.totalAmount)}
            </span>
          </div>
        </div>
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Button
            variant="default"
            size="lg"
            iconName="Download"
            iconPosition="left"
            onClick={handleDownloadReceipt}
            fullWidth
          >
            Download Receipt
          </Button>
          
          <Button
            variant="outline"
            size="lg"
            iconName="Share2"
            iconPosition="left"
            onClick={handleWhatsAppShare}
            fullWidth
          >
            Share via WhatsApp
          </Button>
        </div>

        <Button
          variant="default"
          size="lg"
          iconName="LayoutDashboard"
          iconPosition="left"
          onClick={() => navigate('/user-dashboard')}
          fullWidth
          className="bg-action hover:bg-action/90"
        >
          View My Bookings
        </Button>
      </div>
      <div className="p-4 bg-warning/10 rounded-lg border border-warning/20">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-warning mt-0.5" />
          <div className="flex-1 space-y-2">
            <h4 className="text-sm font-semibold text-foreground">
              Important Information
            </h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Driver details will be shared 2 hours before pickup time</li>
              <li>• Please be ready 10 minutes before scheduled pickup</li>
              <li>• Free cancellation available up to 24 hours before journey</li>
              <li>• For any changes, contact support at +91-79986-66660 or email advaittourtravel666@gmail.com</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="text-center pt-4 border-t border-border">
        <p className="text-sm text-muted-foreground mb-3">
          Need help with your booking?
        </p>
        <div className="flex items-center justify-center gap-3">
          <Button
            variant="outline"
            size="default"
            iconName="Phone"
            iconPosition="left"
            onClick={() => window.open('tel:79986-66660', '_self')}
          >
            Call Support
          </Button>
          
          <Button
            variant="default"
            size="default"
            iconName="MessageCircle"
            iconPosition="left"
            onClick={() => window.open('https://wa.me/917998666660', '_blank')}
            className="whatsapp-pulse"
          >
            WhatsApp Us
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BookingSuccess;